// Range filters for the Scouting table.
//
// Each filter reduces a team to a single number. The bounds are derived from
// the data rather than hardcoded, so a column whose range shifts between
// seasons (predicted wins, returning starters) does not need touching here.
//
// A team with no value for an active filter is excluded. "Predicted wins
// between 8.5 and 10.5" cannot sensibly include a team with no prediction, and
// showing it would make the count on the button wrong.

/** "9-3" and "9-3-1" reduce to a win percentage. Ties count as half. */
export function recordToWinPct(value) {
  if (typeof value !== "string") return null;
  const parts = value.split("-").map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return null;
  const [wins, losses, ties = 0] = parts;
  const games = wins + losses + ties;
  return games > 0 ? ((wins + ties * 0.5) / games) * 100 : null;
}

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export const SCOUTING_FILTERS = [
  {
    key: "predictedWins",
    label: "Predicted wins",
    get: (t) => num(t.predictedWins),
    step: 0.5,
    format: (v) => v.toFixed(1),
  },
  {
    key: "winPct",
    label: "2025 win %",
    get: (t) => recordToWinPct(t.prev_year_record),
    step: 5,
    format: (v) => `${Math.round(v)}%`,
  },
  {
    key: "atsPct",
    label: "2025 vs Margin %",
    get: (t) => recordToWinPct(t.prev_year_ats),
    step: 5,
    format: (v) => `${Math.round(v)}%`,
  },
  {
    key: "powerRank",
    label: "Power rank",
    get: (t) => num(t.powerRank),
    step: 1,
    format: (v) => `#${Math.round(v)}`,
  },
  {
    key: "sos_rank",
    label: "Strength of schedule rank",
    get: (t) => num(t.sos_rank),
    step: 1,
    format: (v) => `#${Math.round(v)}`,
  },
  {
    key: "retStarters",
    label: "Returning starters",
    get: (t) => num(t.retStarters),
    step: 5,
    format: (v) => `${Math.round(v)}%`,
  },
  {
    key: "confOdds",
    label: "Conference odds",
    get: (t) => num(t.confOdds),
    step: 50,
    // American odds: a favourite is negative, so keep the sign visible
    format: (v) => (v > 0 ? `+${Math.round(v)}` : `${Math.round(v)}`),
  },
];

/**
 * The min and max actually present in the data for each filter, snapped
 * outward to the filter's step so the slider ends land on round numbers.
 *
 * Returns null for a filter with fewer than two distinct values, since a
 * slider that cannot move is worse than no slider.
 */
export function deriveBounds(teams) {
  const bounds = {};

  for (const filter of SCOUTING_FILTERS) {
    const values = teams.map(filter.get).filter((v) => v !== null);
    if (!values.length) continue;

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    if (rawMin === rawMax) continue;

    const min = Math.floor(rawMin / filter.step) * filter.step;
    const max = Math.ceil(rawMax / filter.step) * filter.step;

    bounds[filter.key] = { min, max };
  }

  return bounds;
}

/** A range equal to the full bounds is not a filter, it is the default. */
export function isActiveRange(range, bound) {
  if (!range || !bound) return false;
  return range[0] > bound.min || range[1] < bound.max;
}

/** Only the ranges that actually narrow the list. */
export function activeFilters(ranges, bounds) {
  return SCOUTING_FILTERS.filter((f) => isActiveRange(ranges[f.key], bounds[f.key]));
}

/** Applies every active range. Teams missing a filtered value drop out. */
export function applyRanges(teams, ranges, bounds) {
  const active = activeFilters(ranges, bounds);
  if (!active.length) return teams;

  return teams.filter((team) =>
    active.every((filter) => {
      const value = filter.get(team);
      if (value === null) return false;
      const [lo, hi] = ranges[filter.key];
      return value >= lo && value <= hi;
    })
  );
}
